/**
 * Offline Status Hook and Context
 * Provides offline state management across the app
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { offlineSyncService } from '../services/offlineSyncService';
import { API, useAuth } from '../App';
import { toast } from 'sonner';

const OfflineContext = createContext(null);

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within OfflineProvider');
  }
  return context;
};

export const OfflineProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [queueCount, setQueueCount] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize offline service only when user is logged in
  useEffect(() => {
    if (token && user) {
      const initService = async () => {
        try {
          await offlineSyncService.init(API, token);
          setIsInitialized(true);
        } catch (error) {
          console.error('Failed to initialize offline service:', error);
        }
      };
      initService();
    }
  }, [token, user]);

  // Update token when it changes
  useEffect(() => {
    if (token && isInitialized) {
      offlineSyncService.updateToken(token);
    }
  }, [token, isInitialized]);

  // Subscribe to offline events only after initialization
  useEffect(() => {
    if (!isInitialized) return;

    const unsubscribe = offlineSyncService.subscribe((event) => {
      switch (event.type) {
        case 'online':
          setIsOnline(true);
          toast.success('Back online! Syncing data...');
          break;
        case 'offline':
          setIsOnline(false);
          toast.warning('You are offline. Changes will be saved locally.');
          break;
        case 'sync_start':
          setIsSyncing(true);
          setSyncError(null);
          break;
        case 'sync_complete':
          setIsSyncing(false);
          setLastSyncTime(new Date().toISOString());
          updateQueueCount();
          break;
        case 'sync_error':
          setIsSyncing(false);
          setSyncError(event.error?.message || 'Sync failed');
          break;
        case 'action_queued':
          updateQueueCount();
          toast.info('Action saved for sync when online');
          break;
        case 'queue_item_synced':
          updateQueueCount();
          break;
        case 'queue_item_failed':
          toast.error(`Failed to sync: ${event.item?.action} ${event.item?.endpoint}`);
          break;
        default:
          break;
      }
    });

    return unsubscribe;
  }, [isInitialized]);

  const updateQueueCount = useCallback(async () => {
    if (!isInitialized) return;
    try {
      const status = await offlineSyncService.getQueueStatus();
      setQueueCount(status.count);
    } catch (error) {
      console.error('Failed to get queue status:', error);
    }
  }, [isInitialized]);

  // Initial queue count
  useEffect(() => {
    if (isInitialized) {
      updateQueueCount();
    }
  }, [updateQueueCount, isInitialized]);

  // Manual sync trigger
  const triggerSync = useCallback(async () => {
    if (!isOnline || !isInitialized) {
      toast.error('Cannot sync while offline');
      return;
    }
    await offlineSyncService.syncAll();
  }, [isOnline, isInitialized]);

  // Queue an offline action
  const queueOfflineAction = useCallback(async (method, endpoint, data) => {
    if (!isInitialized) return;
    await offlineSyncService.queueAction(method, endpoint, data);
  }, [isInitialized]);

  // Get cached data
  const getCachedAudits = useCallback(async () => {
    if (!user?.id || !isInitialized) return [];
    return offlineSyncService.getCachedAudits(user.id);
  }, [user?.id, isInitialized]);

  const getCachedTemplates = useCallback(async () => {
    if (!isInitialized) return [];
    return offlineSyncService.getCachedTemplates();
  }, [isInitialized]);

  const getCachedCalendarEvents = useCallback(async () => {
    if (!isInitialized) return [];
    return offlineSyncService.getCachedCalendarEvents();
  }, [isInitialized]);

  // Get storage info
  const getStorageInfo = useCallback(async () => {
    if (!isInitialized) return null;
    return offlineSyncService.getStorageInfo();
  }, [isInitialized]);

  // Clear cache
  const clearCache = useCallback(async () => {
    if (!isInitialized) return;
    await offlineSyncService.clearAllCache();
    toast.success('Offline cache cleared');
  }, [isInitialized]);

  return (
    <OfflineContext.Provider
      value={{
        isOnline,
        isSyncing,
        queueCount,
        lastSyncTime,
        syncError,
        isInitialized,
        triggerSync,
        queueOfflineAction,
        getCachedAudits,
        getCachedTemplates,
        getCachedCalendarEvents,
        getStorageInfo,
        clearCache,
      }}
    >
      {children}
    </OfflineContext.Provider>
  );
};

export default OfflineProvider;
